<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'
import { computed, ref, watch } from 'vue'
import ChevronDown from '/svg/chevron-down.svg'

interface Option {
  label: string
  value: string | number
  icon?: string
}

interface Props {
  modelValue: (string | number)[]
  label?: string
  placeholder?: string
  disabled?: boolean
  error?: string
  searchable?: boolean
  options: Option[]
  variant?: 'default' | 'checkbox'
  appearance?: string
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  searchable: false,
  variant: 'default',
  appearance: '',
})

const emit = defineEmits<(e: 'update:modelValue', value: (string | number)[]) => void>()

const isFocused = ref(false)
const isOpen = ref(false)
const wrapper = ref<HTMLElement | null>(null)
const searchQuery = ref('')
const selected = ref<(string | number)[]>([...props.modelValue])

onClickOutside(wrapper, () => {
  isOpen.value = false
  searchQuery.value = ''
})

const filteredOptions = computed(() => {
  if (!props.searchable || !searchQuery.value) return props.options
  return props.options.filter((o) => o.label.toLowerCase().includes(searchQuery.value.toLowerCase()))
})

const displayText = computed(() => {
  if (selected.value.length === 0) return props.placeholder || ''

  const selectedLabels = selected.value.map((val) => {
    const option = props.options.find((o) => o.value === val)
    return option?.label || ''
  })

  return selectedLabels.join('، ')
})

function toggleOpen() {
  if (props.disabled) return
  isOpen.value = !isOpen.value

  if (isOpen.value && props.searchable) {
    setTimeout(() => {
      const el = document.getElementById('search-input')
      if (el) el.focus()
    }, 100)
  } else {
    searchQuery.value = ''
  }
}

function updateValue(value: string | number) {
  const idx = selected.value.indexOf(value)

  if (idx === -1) {
    selected.value.push(value)
  } else {
    selected.value.splice(idx, 1)
  }

  emit('update:modelValue', [...selected.value])
}

watch(
  () => props.modelValue,
  (newVal) => {
    selected.value = [...newVal]
  }
)
</script>

<template>
    <div class="relative flex flex-col gap-1 w-full h-[71.47px]" ref="wrapper">
        <!-- Label -->
        <label v-if="label" class="w-fit bg-white px-1 transition-all z-30 duration-400 pointer-events-none" :class="[(isFocused || selected.length > 0)
            ? 'absolute ms-4 -mt-2 text-[10px] font-bold'
            : 'absolute mt-[13px] ms-5 text-[13px] font-medium',
        appearance === 'admin' ? 'text-[#6C6C6C]' : 'text-[#1b325a]']">
            {{ label }}
        </label>

        <!-- Custom Select Wrapper -->
        <div class="relative w-full h-12">
            <div class="w-full h-full rounded-xl border px-[18px] pr-8 text-[13px] font-medium outline-none transition
                  border-[#E0E0E0] focus:outline-none focus:ring-0 focus:border-[#000]
                  bg-white flex items-center" :class="[
                    disabled ? 'cursor-not-allowed text-[#ABBAD5]' : 'text-[#1A1A1A] cursor-pointer'
                ]" @click="toggleOpen">
                <span class="flex items-center gap-2 truncate">
                    {{ displayText }}
                </span>
            </div>

            <!-- Chevron Icon -->
            <img :src="ChevronDown" alt="chevron"
                class="absolute top-1/2 end-0.5 -translate-y-1/2 w-6 h-6 pointer-events-none transition-transform duration-200"
                :class="{ 'rotate-180': isOpen }" />

            <!-- Custom Dropdown Options -->
            <Transition name="fade-slide" appear>
                <div v-if="isOpen"
                    class="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E0E0E0] rounded-xl shadow-lg z-[999] max-h-60 overflow-y-auto custom-dropdown">

                    <!-- Search Input (only when searchable is true) -->
                    <div v-if="searchable" class="sticky top-0 bg-white p-2 border-b border-[#E0E0E0]">
                        <input id="search-input" type="text" v-model="searchQuery" placeholder="جستجو..."
                            class="w-full px-3 py-2 text-sm border border-[#E0E0E0] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1b325a]"
                            @keydown.esc="isOpen = false; searchQuery = ''" />
                    </div>

                    <!-- Options List -->
                    <div v-if="filteredOptions.length > 0">
                        <div v-for="opt in filteredOptions" :key="opt.value"
                            class="px-4 py-3 text-[#1A1A1A]  cursor-pointer transition-all duration-200 text-[15px] font-medium flex items-center gap-2"
                            :class="[
                                selected.includes(opt.value)
                                    ? ' font-bold'
                                    : ' hover:bg-[#f8f9fa]'
                            ]" @click="updateValue(opt.value)">

                            <template v-if="props.variant === 'checkbox'">
                                <div class="w-5 h-5 rounded-md border-2 
                            flex items-center justify-center duration-200" :class="selected.includes(opt.value)
                                ? 'border-[#2626BF] bg-[#2626BF]'
                                : 'border-[#2626BF]'">
                                    <svg v-if="modelValue" class="w-3 h-3 text-white" viewBox="0 0 11 8" fill="none"
                                        xmlns="http://www.w3.org/2000/svg">
                                        <path d="M9.5 0.75L3.11625 7L0.75 4.58875" stroke="currentColor"
                                            stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                    </svg>
                                </div>
                            </template>

                            <img v-if="opt.icon" :src="opt.icon" alt="icon" class="w-5 h-5 shrink-0" />
                            {{ opt.label }}
                        </div>
                    </div>
                    <div v-else class="px-4 py-3 text-[15px] text-gray-500 text-center">
                        موردی یافت نشد
                    </div>
                </div>
            </Transition>
        </div>

        <!-- Hidden Native Select for Form Submission -->
        <select multiple :disabled="disabled" :value="modelValue" class="hidden">
            <option v-for="opt in options" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
        </select>

        <!-- Error -->
        <div v-if="error" class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] mt-1">
            <img src="/svg/error-icon.svg" width="16" alt="error-icon" />{{ error }}
        </div>
    </div>
</template>

<style scoped>
/* Custom scrollbar for dropdown */
.custom-dropdown::-webkit-scrollbar {
    width: 6px;
}

.custom-dropdown::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.custom-dropdown::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
}

.custom-dropdown::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
    transition: all 0.2s ease-out;
}

.fade-slide-enter-from {
    opacity: 0;
    transform: translateY(-6px);
}

.fade-slide-leave-to {
    opacity: 0;
    transform: translateY(-6px);
}
</style>